// v0.5.0 lot G3 — missions de la colonie : lancer, lister, piloter, approuver le plan,
// arrêter, lire le tableau noir et le dossier de preuves, décider sur escalade.
// L'identité (demandeur, approbateur) vient du jeton côté serveur — jamais transmise ici.
import { request } from './http'
import type {
  MissionActionResult, MissionBudgetInput, MissionDecisionInput, MissionDetail, MissionEventType,
  MissionEventsPage, MissionEvidence, MissionGenerationSubject, MissionHarnessPolicy, MissionIterationTarget, MissionLaunchInput, MissionLaunchResult,
  MissionPauseResult, MissionReplanDecisionInput, MissionResumeResult, MissionSummary,
} from './types/missions'

const enc = encodeURIComponent

export const missionsApi = {
  listMissions: (workspaceId: string) =>
    request<MissionSummary[]>(`/missions?workspace_id=${enc(workspaceId)}`),

  launchMission: (input: MissionLaunchInput) =>
    request<MissionLaunchResult>('/missions', { method: 'POST', body: JSON.stringify(input) }),

  getMission: (missionId: string) => request<MissionDetail>(`/missions/${enc(missionId)}`),

  /** Mode par défaut et modes permis par le gabarit de l'espace (ADR-0108 : le gabarit décide). */
  getMissionHarnessPolicy: (workspaceId: string) =>
    request<MissionHarnessPolicy>(`/missions/harness-policy?workspace_id=${enc(workspaceId)}`),

  /**
   * RJ-51 : applications LIVRÉES de l'espace sur lesquelles un sprint ou une correction
   * travaillerait (révision de base) et l'itération en cours qui occupe chacune.
   */
  getMissionIterationTargets: (workspaceId: string) =>
    request<{ applications: MissionIterationTarget[] }>(`/missions/iteration-targets?workspace_id=${enc(workspaceId)}`),

  /**
   * RJ-58 : spécifications approuvées qu'une génération d'application peut viser, avec leurs
   * items de backlog acceptés et le refus (`MISSION_SUBJECT_EMPTY`) que le lancement opposerait.
   */
  getMissionGenerationSubjects: (workspaceId: string) =>
    request<{ specifications: MissionGenerationSubject[] }>(`/missions/generation-subjects?workspace_id=${enc(workspaceId)}`),

  /**
   * Porte de départ : approuver le plan RELU (son empreinte `plan_sha256_current`) ET le budget
   * (pas le demandeur), ou refuser avec motif. Un plan changé depuis la lecture est refusé (409).
   */
  decideMissionPlan: (missionId: string, decision: 'approved' | 'rejected', comment?: string,
                      budget?: MissionBudgetInput, planSha256?: string) =>
    request<MissionActionResult>(`/missions/${enc(missionId)}/plan/decision`, {
      method: 'POST',
      body: JSON.stringify({ decision, ...(comment ? { comment } : {}), ...(budget ? { budget } : {}),
        ...(planSha256 ? { plan_sha256: planSha256 } : {}) }),
    }),

  /** Bouton « Arrêter » : le job en cours finit son étape, rien d'autre ne démarre. */
  stopMission: (missionId: string, comment?: string) =>
    request<MissionActionResult>(`/missions/${enc(missionId)}/stop`, {
      method: 'POST', body: JSON.stringify(comment ? { comment } : {}),
    }),

  /**
   * SRF-PRJ-008 — pause VOLONTAIRE : l'étape en cours finit, rien d'autre ne démarre, plus aucune
   * minute de GPU. Refus 409 nommés : MISSION_NOT_RUNNING, MISSION_ALREADY_SUSPENDED, MISSION_ALREADY_PAUSED.
   */
  pauseMission: (missionId: string, comment?: string) =>
    request<MissionPauseResult>(`/missions/${enc(missionId)}/pause`, {
      method: 'POST', body: JSON.stringify(comment ? { comment } : {}),
    }),

  /** SRF-PRJ-008 — reprendre une mission en pause : elle repart au tour suivant (409 MISSION_NOT_PAUSED). */
  resumeMission: (missionId: string) =>
    request<MissionResumeResult>(`/missions/${enc(missionId)}/resume`, { method: 'POST', body: JSON.stringify({}) }),

  /** Tableau noir, du plus récent au plus ancien, paginé côté serveur. */
  listMissionEvents: (missionId: string,
                      page: { limit?: number; offset?: number; type?: MissionEventType; taskId?: string } = {}) => {
    const params = new URLSearchParams()
    if (page.limit != null) params.set('limit', String(page.limit))
    if (page.offset != null) params.set('offset', String(page.offset))
    if (page.type) params.set('type', page.type)
    if (page.taskId) params.set('task_id', page.taskId)
    const q = params.toString()
    return request<MissionEventsPage>(`/missions/${enc(missionId)}/events${q ? `?${q}` : ''}`)
  },

  getMissionTaskEvidence: (missionId: string, taskId: string) =>
    request<MissionEvidence>(`/missions/${enc(missionId)}/tasks/${enc(taskId)}/evidence`),

  /**
   * FND-V050-038 : approuver (empreinte relue, pas le demandeur) ou refuser (motif) le nouveau
   * découpage proposé par la reine ; approuvé, la passerelle l'applique.
   */
  decideMissionReplan: (missionId: string, input: MissionReplanDecisionInput) =>
    request<MissionActionResult>(`/missions/${enc(missionId)}/replan/decision`, {
      method: 'POST', body: JSON.stringify(input),
    }),

  /** Décision humaine sur une tâche arrêtée ou une escalade (boîte d'attention). */
  decideMissionItem: (missionId: string, input: MissionDecisionInput) =>
    request<MissionActionResult>(`/missions/${enc(missionId)}/decisions`, {
      method: 'POST', body: JSON.stringify(input),
    }),
}
